import React, { useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";
import AOS from "aos";
import "aos/dist/aos.css";
import classNames from "classnames";

const useStyles = makeStyles((theme) => ({
  container: {
    zIndex: 1,
    width: "100%",
    maxWidth: "1170px",
    marginLeft: "auto",
    marginRight: "auto",
    padding: "5em 2em",
    position: "relative",
    "& p": {
      fontSize: "18px",
      color: "#000",
      margin: 0,

      [theme.breakpoints.down("md")]: {
        fontSize: "18px",
      },
      [theme.breakpoints.down("md")]: {
        fontSize: "16px",
      },
    },
    "& h3": {
      fontSize: "36px",
      fontWeight: 600,
      margin: 0,
      [theme.breakpoints.down("md")]: {
        fontSize: "30px",
        letterSpacing: "-1px",
      },
      [theme.breakpoints.down("sm")]: {
        fontSize: "25px",
      },
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: "1350px",
    },
    "@media screen and (max-width: 767px)": {
      paddingLeft: "1em",
      paddingRight: "1em",
    },
  },
  mainHeading: {
    width: "100%",
    textAlign: "center",
    letterSpacing: "-3.6px",
    fontSize: "92px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    lineHeight: 1.1,
    display: "block",

    [theme.breakpoints.down("md")]: {
      letterSpacing: "-2px",
      fontSize: "80px",
    },
    [theme.breakpoints.down("sm")]: {
      letterSpacing: "-1px",
      fontSize: "34px",
    },
  },
  headingText: {
    width: "100%",
    maxWidth: "800px",
    color: "#606060",
    marginBottom: "20px",
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
    fontSize: "22px",
  },
  gradientText: {
    backgroundImage: "linear-gradient(to right, #6557ff, #aa3fff 80%)",
    textAlign: "center",
    backgroundClip: "text",
    "-webkit-text-fill-color": "transparent",
  },
  imageHolder: {
    backgroundColor: "rgba(80, 48, 229, .06)",
    border: "1px dashed rgba(80, 48, 229, .59)",
    borderBottomStyle: "none",
    borderTopLeftRadius: "10px",
    borderTopRightRadius: "10px",
    padding: "8px",
    paddingBottom: 0,
    marginTop: "auto",
    position: "relative",

    [theme.breakpoints.down("md")]: {
      marginTop: "20px",
    },

    "& img": {
      width: "100%",
      height: "100%",
      borderRadius: "10px",
    },
  },
  profileImage: {
    width: "81px",
    backgroundColor: "#fff",
    borderRadius: "100px",
    padding: "3px",
    position: "relative",

    "& div": {
      width: "100%",
      paddingTop: "100%",
      position: "relative",
    },

    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "50px",
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
  },
  cardContainer: {
    display: "flex",
    gap: "3rem",
    marginTop: "2em",
    padding: "2em 0",

    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      marginTop: "0",
    },
  },
  cardContainer2: {
    display: "flex",
    gap: "2rem",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",

      "& img": {
        marginTop: "20px",
      },
    },
  },
  card: {
    background: "#f8f9fc",
    padding: "2em 2.5em",
    paddingBottom: 0,
    borderRadius: "30px",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("md")]: {
      padding: "1.5em",
      paddingBottom: 0,
    },
  },
  cardSm: {
    paddingBottom: "1em",

    [theme.breakpoints.down("md")]: {
      maxWidth: "unset",
    },
  },
}));

export default function FeaturesSection() {
  const classes = useStyles();

  useEffect(() => {
    AOS.init();
  }, []);

  return (
    <Box className={classes.container}>
      <div>
        <Typography
          data-aos="fade-up"
          data-aos-easing="ease-out"
          data-aos-delay="100"
          className={classes.mainHeading}
          variant="h2"
          fontWeight={500}
          textAlign="center"
        >
          Leveraging AI for Scalable &nbsp;
          <span className={classes.gradientText}>
            University Admissions Success
          </span>
        </Typography>
        <div
          data-aos="fade-up"
          data-aos-easing="ease-out"
          data-aos-delay="300"
          style={{
            maxWidth: "690px",
            padding: "1rem 0",
          }}
        >
          <p>
            Our AI-driven platform provides a scalable solution to admissions
            preparation, with significant market potential and a growing user
            base.
          </p>
        </div>
      </div>
      <div className={classes.cardContainer}>
        <div
          style={{
            flexBasis: "60%",
          }}
          className={classes.card}
          data-aos="fade-up"
          data-aos-easing="ease-out"
          data-aos-delay="550"
        >
          <h3>Quantify Performance Potential</h3>
          <p style={{ marginBottom: "1rem" }}>
            Our platform delivers a comprehensive scoring system, rated out of
            40 and segmented into specific sub-criteria, offering precise
            insights into candidate performance.

          </p>
          <p style={{ marginBottom: "2rem" }}>
            Candidates gain access to a
            scalable solution designed to optimize their readiness for the
            admissions process. Our model is trained on real admissions data
            ensuring accuracy.
          </p>

          <div
            style={{
              maxWidth: "70%",
              maxHeight: "100%",
              margin: "auto",
              marginBottom: 0,
            }}
            className={classes.imageHolder}
          >
            <img
              style={{ maxWidth: "100%", maxHeight: "100%", margin: "auto" }}
              width="450"
              height="571"
              alt="Result page"
              src="images/logged_out/result.webp"
            />
          </div>
        </div>
        <div
          style={{
            flexBasis: "35%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "2rem",
          }}
        >
          <div
            className={classNames(classes.card, classes.cardSm)}
            data-aos="fade-up"
            data-aos-easing="ease-out"
            data-aos-delay="550"
          >
            <h3>Innovation Backed by Expertise</h3>
            <p>
              Auralyze is designed by experts in education and technology
            </p>

            <div
              style={{
                display: "flex",
                marginTop: "2em",
                justifyContent: "center",
              }}
            >
              <div data-aos="zoom-in" className={classes.profileImage}>
                <div>
                  <img
                    width="75"
                    height="75"
                    alt="Avatar 1"
                    src="images/logged_out/avatar-1.webp"
                  />
                </div>
              </div>
              <div
                data-aos="zoom-in"
                style={{ marginLeft: "-1em" }}
                className={classes.profileImage}
              >
                <div>
                  <img
                    width="75"
                    height="75"
                    alt="Avatar 2"
                    src="images/logged_out/avatar-2.webp"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={classes.card}>
            <div
              data-aos="fade-up"
              data-aos-easing="ease-out"
              data-aos-delay="550"
            >
              <h3>Commitment to Continuous Innovation</h3>
              <p>
                Our platform evolves with the market, incorporating the latest
                AI advancements to stay ahead of industry trends and enhance
                user success rates.
              </p>
            </div>
            <div data-aos="fade-up" className={classes.imageHolder}>
              <img
                style={{ maxWidth: "100%" }}
                src="images/logged_out/blogs.webp"
                width="750"
                height="490"
                alt="Blogs page"
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.cardContainer2}>
        <div
          data-aos="fade-up"
          data-aos-easing="ease-out"
          data-aos-delay="550"
          className={classes.card}
        >
          <h3>
            Leverage a Comprehensive Question Bank for Enhanced Candidate
            Preparation
          </h3>
          <p>
            Equipping candidates with an extensive collection of Medicine and
            Dental interview questions meticulously curated from diverse topics.
            Our database covers Ethics, Role-play scenarios, Critical Thinking
            exercises, and beyond, providing a holistic approach to preparing
            for university admissions.
          </p>
          <div
            style={{ marginTop: "1rem" }}
            className={classes.imageHolder}
            data-aos="fade-up"
            data-aos-easing="ease-out"
            data-aos-delay="550"
          >
            <img
              src="images/logged_out/interview-question.png"
              alt="Example interview question"
            />
          </div>
        </div>
      </div>
    </Box>
  );
}
